// Helpers
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { unformattedAmount } from "@/Common/helpers/number";
import { getContentType } from "@/Common/helpers/objects";
import { getDateFormat, getFormattedTime, normalizeTime, removeMeridian } from "@/Common/helpers/datetime";

const isDate = (str) => !isNaN(Date.parse(str));
const isEmpty = (value) => value === "" || value === null || value === undefined;
const isIndeedNumber = (value) => !isEmpty(value) && (isNumeric(unformattedAmount(value)) || isNumeric(value) || isNumeric(Number(value)));
const isStrictTime = (value) => value?.includes(":") && !value?.includes("/") && !value?.includes("-");

const transformValue = (value, isUpperCase = false) => {
	if (isUpperCase) {
		return String(value)?.toUpperCase()?.trim();
	}
	return String(value)?.toLowerCase()?.trim();
};

// Special match conditions
const isMatchNestedValue = ({ original, value, excludeKeys, isInNested }) => {
	if (!isInNested || !original?.length) {
		return false;
	}
	return original.some((row) => isRowMatchSearch({ row, value, excludeKeys }));
};

const isMatchString = (original, value) =>
	transformValue(original) === transformValue(value) || transformValue(original).includes(transformValue(value));

const isMatchFormatDate = (
	original,
	value,
	formats = [{ year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }]
) => {
	if (!isDate(value)) {
		return false;
	}

	const date = new Date(original);
	const valueDate = new Date(value);

	const isMatchFormatted = (first, second, format) =>
		getDateFormat(first, { options: format }) === getDateFormat(second, { options: format });

	return formats.some((format) => isMatchFormatted(date, valueDate, format)) && String(original).includes(String(value));
};

const isMatchHours = (original, value, formats = [{ hour: "2-digit", minute: "2-digit" }]) => {
	if (!isStrictTime(value)) {
		return false;
	}

	const checkValue = normalizeTime(transformValue(value));
	const date = new Date(original);
	const isAppendRequired = checkValue?.includes("am") || checkValue?.includes("pm");

	return formats.some((format) => {
		let formatted = getFormattedTime(date, { options: format });
		let formattedUTC = getFormattedTime(date, { options: { ...format, timeZone: "UTC" } });

		formatted = normalizeTime(transformValue(formatted));
		formattedUTC = normalizeTime(transformValue(formattedUTC));

		if (isAppendRequired) {
			return formatted === checkValue || formattedUTC === checkValue;
		}

		return (
			(removeMeridian(formatted) === removeMeridian(checkValue) || removeMeridian(formattedUTC) === removeMeridian(checkValue)) &&
			String(original).includes(String(value))
		);
	});
};

const isMatchDate = (original, value) =>
	isMatchHours(original, value) || isMatchFormatDate(original, value) || isMatchString(original, value);

const isMatchNumber = (original, value, args) => {
	const { round } = args || {};
	let isMatchRound = false;
	const isExactMatch = Number(original) === Number(value);
	const isUnformattedMatch = unformattedAmount(original)?.includes(unformattedAmount(value));
	if (round) {
		const roundOriginal = Number(unformattedAmount(original)).toFixed(round);
		const roundValue = Number(unformattedAmount(value)).toFixed(round);
		isMatchRound = isMatchString(roundOriginal, roundValue);
	}
	return isExactMatch || isUnformattedMatch || isMatchRound || isMatchString(original, value);
};

const getCellDefinition = (key, args) => {
	const { original, nestedKeys = [], columns = [] } = args || {};
	const column = columns.find((col) => col.field === key);
	const type = column?.type ? transformValue(column.type, true) : getContentType(original);
	const isInNested = type === "ARRAY" && nestedKeys?.includes(key);
	return {
		...column,
		...args,
		type,
		isInNested,
		field: key,
	};
};

const isExcludedKey = (key, excludeKeys = []) => excludeKeys.some((excluded) => excluded === key);

const isMatchValue = (args) => {
	const { original, value, excludeKeys, isInNested, nestedKeys, type, round, valueFormatter } = args || {};

	if (isEmpty(original)) {
		return false;
	}

	switch (type) {
		case "STRING":
		case "NUMBER": {
			if (isIndeedNumber(value)) {
				return isMatchNumber(original, value, { round });
			}
			if (isDate(original)) {
				return isMatchDate(applyValueFormatter(original, valueFormatter), value);
			}
			return isMatchString(original, value);
		}
		case "DATE":
			return isMatchDate(applyValueFormatter(original, valueFormatter), value);
		case "OBJECT":
			return isRowMatchSearch({ row: applyValueFormatter(original, valueFormatter), value, excludeKeys, nestedKeys });
		case "ARRAY":
			return isMatchNestedValue({ original: applyValueFormatter(original, valueFormatter), value, excludeKeys, isInNested });
		default:
			return false;
	}
};

function applyValueFormatter(value, formatter) {
	return formatter ? formatter(value) : value;
}

const isRowMatchSearch = ({ row, value, columns, excludeKeys = [], nestedKeys = [] }) => {
	if (!row || isEmpty(value)) {
		return false;
	}

	return Object.keys(row).some((key) => {
		const cellValue = row?.[key];
		const isUnavailable = isExcludedKey(key, excludeKeys);

		// Don't search in the excluded keys and avoid empty columns
		if (isUnavailable || !cellValue) {
			return false;
		}

		const definition = getCellDefinition(key, { excludeKeys, nestedKeys, columns, value, original: cellValue });
		return isMatchValue({ ...definition });
	});
};

export { isMatchNumber, isMatchString, isRowMatchSearch, transformValue };
